import { computed, inject, Injectable, signal } from '@angular/core';
import { BROWSER_STORAGE } from './browser-storage';

// Acces d'un joueur a sa mission : le jeton n'est remis qu'une fois (TD-009),
// le front le garde en localStorage. codeName est celui saisi a l'inscription.
export interface PlayerSession {
  missionId: number;
  agentToken: string;
  codeName: string | null;
}

const STORAGE_KEY = 'killer-party.player-sessions';

@Injectable({ providedIn: 'root' })
export class PlayerSessionService {
  private readonly storage = inject(BROWSER_STORAGE);
  readonly sessions = signal<PlayerSession[]>(readSessions(this.storage));
  // Derniere session enregistree : la mission a reprendre depuis l'accueil.
  readonly latest = computed(() => this.sessions().at(-1) ?? null);

  save(session: PlayerSession): void {
    const previous = this.find(session.missionId);
    const saved = { ...session, codeName: session.codeName ?? previous?.codeName ?? null };
    this.write([
      ...this.sessions().filter(({ missionId }) => missionId !== session.missionId),
      saved,
    ]);
  }

  tokenFor(missionId: number): string | null {
    return this.find(missionId)?.agentToken ?? null;
  }

  codeNameFor(missionId: number): string | null {
    return this.find(missionId)?.codeName ?? null;
  }

  // Nom de code appris apres coup (acces regenere, TD-017) : sans session, rien a retenir.
  rememberCodeName(missionId: number, codeName: string): void {
    const session = this.find(missionId);
    if (session && session.codeName !== codeName) {
      this.write(this.sessions().map((s) => (s.missionId === missionId ? { ...s, codeName } : s)));
    }
  }

  remove(missionId: number): void {
    this.write(this.sessions().filter((session) => session.missionId !== missionId));
  }

  private find(missionId: number): PlayerSession | undefined {
    return this.sessions().find((session) => session.missionId === missionId);
  }

  private write(sessions: PlayerSession[]): void {
    this.sessions.set(sessions);
    try {
      this.storage?.setItem(STORAGE_KEY, JSON.stringify(sessions));
    } catch {
      // Stockage indisponible (navigation privee) : la session ne vit que dans l'onglet.
    }
  }
}

function readSessions(storage: Storage | null): PlayerSession[] {
  try {
    const stored: unknown = JSON.parse(storage?.getItem(STORAGE_KEY) ?? '[]');
    return Array.isArray(stored) ? stored.filter(isSession) : [];
  } catch {
    return [];
  }
}

function isSession(value: unknown): value is PlayerSession {
  const session = value as PlayerSession;
  return (
    typeof session?.missionId === 'number' &&
    typeof session.agentToken === 'string' &&
    (session.codeName === null || typeof session.codeName === 'string')
  );
}
